import { useState, useRef } from "react";
import { ChevronLeft, Download, Printer } from "lucide-react";
import { TINTA, MARCA, ACCION, MONO } from "../constants.js";
import { fechaCorta } from "../lib/util.js";

/* ============================================================
   INFORME — compila las capturas y las exporta a Word (.doc) y PDF.
   ============================================================ */

function generarHtmlInforme(capturas, nombreProyecto) {
  const hoy = new Date().toLocaleDateString("es", { dateStyle: "long" });
  const items = capturas
    .map(
      (c, i) => `
    <div style="page-break-inside:avoid; margin-bottom:32px; border:1px solid #D8DCE3; border-radius:8px; overflow:hidden;">
      <div style="background:#17223B; color:#fff; padding:10px 16px; font-family:monospace; font-size:12px;">
        REV-${String(i + 1).padStart(3, "0")} · ${fechaCorta(c.fecha)}${
        c.url ? " · " + c.url : ""
      }
      </div>
      <div style="padding:16px;">
        <h3 style="margin:0 0 8px; font-size:16px; color:#17223B;">${c.titulo}</h3>
        ${
          c.nota
            ? `<p style="margin:0 0 12px; font-size:13px; color:#3A4459;"><b>Indicación:</b> ${c.nota}</p>`
            : ""
        }
        <img src="${c.imagen}" style="max-width:100%; border:1px solid #E2E5EB; border-radius:4px;" />
      </div>
    </div>`
    )
    .join("");
  return `<html><head><meta charset="utf-8"><title>${nombreProyecto}</title></head>
  <body style="font-family:Arial,sans-serif; max-width:800px; margin:0 auto; padding:24px;">
    <div style="border-bottom:3px solid #D6453D; padding-bottom:16px; margin-bottom:28px;">
      <p style="font-family:monospace; font-size:11px; color:#6B7280; margin:0;">INFORME DE REVISIÓN VISUAL</p>
      <h1 style="margin:4px 0 0; font-size:26px; color:#17223B;">${nombreProyecto}</h1>
      <p style="margin:6px 0 0; font-size:13px; color:#3A4459;">${hoy} · ${
    capturas.length
  } observación(es)</p>
    </div>
    ${items}
  </body></html>`;
}

export default function Informe({ capturas, onVolver }) {
  const [nombre, setNombre] = useState("Revisión de sitio web");
  const printRef = useRef(null);

  function descargarWord() {
    const html = generarHtmlInforme(capturas, nombre);
    const blob = new Blob(["﻿", html], { type: "application/msword" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "informe-revision.doc";
    a.click();
    URL.revokeObjectURL(a.href);
  }

  function imprimirPdf() {
    const html = generarHtmlInforme(capturas, nombre);
    const w = window.open("", "_blank");
    if (!w) {
      alert("Permite las ventanas emergentes para generar el PDF.");
      return;
    }
    w.document.write(html);
    w.document.close();
    setTimeout(() => w.print(), 400);
  }

  return (
    <div className="h-full overflow-auto" style={{ background: "#E8EAEF" }}>
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <button
            onClick={onVolver}
            className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium bg-white shadow-sm"
            style={{ color: TINTA }}
          >
            <ChevronLeft size={16} /> Volver
          </button>
          <div className="flex-1" />
          <button
            onClick={descargarWord}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm"
            style={{ background: TINTA }}
          >
            <Download size={16} /> Word (.doc)
          </button>
          <button
            onClick={imprimirPdf}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm"
            style={{ background: ACCION }}
          >
            <Printer size={16} /> PDF / Imprimir
          </button>
        </div>

        <div ref={printRef} className="bg-white rounded-xl shadow-sm p-6 sm:p-8">
          <p
            className="text-xs tracking-widest"
            style={{ fontFamily: MONO, color: "#6B7280" }}
          >
            INFORME DE REVISIÓN VISUAL
          </p>
          <input
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            className="text-2xl font-bold w-full mt-1 mb-1 outline-none border-b border-transparent focus:border-gray-300"
            style={{ color: TINTA }}
          />
          <p
            className="text-sm mb-6 pb-4 border-b-4"
            style={{ color: "#3A4459", borderColor: MARCA }}
          >
            {new Date().toLocaleDateString("es", { dateStyle: "long" })} ·{" "}
            {capturas.length} observación(es)
          </p>
          {capturas.map((c, i) => (
            <div
              key={c.id}
              className="mb-8 border rounded-lg overflow-hidden"
              style={{ borderColor: "#D8DCE3" }}
            >
              <div
                className="px-4 py-2 text-xs text-white"
                style={{ background: TINTA, fontFamily: MONO }}
              >
                REV-{String(i + 1).padStart(3, "0")} · {fechaCorta(c.fecha)}
                {c.url ? ` · ${c.url}` : ""}
              </div>
              <div className="p-4">
                <h3 className="font-semibold mb-1" style={{ color: TINTA }}>
                  {c.titulo}
                </h3>
                {c.nota && (
                  <p className="text-sm mb-3" style={{ color: "#3A4459" }}>
                    <b>Indicación:</b> {c.nota}
                  </p>
                )}
                <img
                  src={c.imagen}
                  alt={c.titulo}
                  className="w-full rounded border"
                  style={{ borderColor: "#E2E5EB" }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
